import type { PageHelp } from '../../help/types';

/** Guide for `/projects/:code/settings` — locales, packages, publish targets and export options. */
export const projectSettingsHelp: PageHelp = {
  title: 'Project settings',
  intro:
    'Locales, package subscriptions, publish targets and export options of one project. Each of them changes what ends up in the published files, so only the project owner and administrators can change them; everyone else sees them read-only.',
  sections: [
    'Reading the page',
    'Locales and packages',
    'Publish targets',
    'New target dialog',
    'Edit target dialog',
    'Export options',
    'Coverage and prefixes',
  ],
  contexts: {
    'target-dialog':
      'Click “Add target” on the “Publish targets” card (project owners and administrators only).',
    'target-edit-dialog':
      'Click the pencil next to a target on the “Publish targets” card (project owners and administrators only).',
  },
  tips: [
    'Every change on this page is saved the moment you make it — there is no Save button. The description is the one exception: it is saved when you leave the field.',
    'Locales, package subscriptions and export options change the files only at the next publish. Each of them raises the content version, so a target with auto-publish switched on publishes the change by itself.',
  ],
  steps: [
    // ── Reading the page ────────────────────────────────────────────────────────
    {
      selector: '[data-help="project-settings-header"]',
      title: 'Read the project header',
      body: 'Shows the project code, its source locale and the content version. The content version goes up with every change that affects the exported files; auto-publish compares it with the last release to decide whether there is something new to publish.',
      section: 'Reading the page',
      i18n: {
        'de-AT': {
          title: 'Die Projektkopfzeile lesen',
          body: 'Zeigt den Projektcode, die Ausgangssprache und die Inhaltsversion („content version“). Die Inhaltsversion steigt mit jeder Änderung, die die exportierten Dateien betrifft; Auto-Publish vergleicht sie mit dem letzten Release, um zu entscheiden, ob es etwas Neues zu veröffentlichen gibt.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje zaglavlja projekta',
          body: 'Prikazuje kod projekta, izvorni jezik i verziju sadržaja („content version“). Verzija sadržaja raste sa svakom promenom koja utiče na izvezene datoteke; automatsko objavljivanje je poredi sa poslednjim izdanjem da bi odlučilo da li ima nečeg novog za objavljivanje.',
        },
      },
    },

    // ── Locales and packages ────────────────────────────────────────────────────
    {
      selector: '[data-help="project-settings-locales"]',
      title: 'Choose the project’s locales',
      body: 'Pick every language this project is translated into; each pick or removal is saved at once. The source locale is the highlighted chip and cannot be removed, because a key with no source value is never exported. Dropping a locale takes it out of every future bundle, while the translations already stored for it are kept.',
      section: 'Locales and packages',
      i18n: {
        'de-AT': {
          title: 'Die Sprachen des Projekts wählen',
          body: 'Wählen Sie jede Sprache, in die dieses Projekt übersetzt wird; jedes Hinzufügen oder Entfernen wird sofort gespeichert. Die Ausgangssprache ist der hervorgehobene Chip und kann nicht entfernt werden, weil ein Key ohne Ausgangswert nie exportiert wird. Eine entfernte Sprache fehlt in allen künftigen Bundles, die bereits gespeicherten Übersetzungen bleiben aber erhalten.',
        },
        'sr-Latn-RS': {
          title: 'Izbor jezika projekta',
          body: 'Izaberite svaki jezik na koji se ovaj projekat prevodi; svako dodavanje ili uklanjanje se odmah čuva. Izvorni jezik je istaknuta oznaka i ne može se ukloniti, jer se ključ bez izvorne vrednosti nikada ne izvozi. Uklonjeni jezik nestaje iz svih budućih paketa datoteka, ali već sačuvani prevodi za njega ostaju.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-packages"]',
      title: 'Subscribe to shared packages',
      body: 'A subscribed package adds its keys to this project’s bundles, for the locales the package itself carries; each pick or removal is saved at once. Package keys belong to the package: editing one from this project without owner rights on the package creates a suggestion instead of a change. A single key of another project is not a package: link it on the Linked keys page.',
      section: 'Locales and packages',
      i18n: {
        'de-AT': {
          title: 'Gemeinsame Pakete abonnieren',
          body: 'Ein abonniertes Paket fügt seine Keys den Bundles dieses Projekts hinzu, und zwar für die Sprachen, die das Paket selbst führt; jedes Hinzufügen oder Entfernen wird sofort gespeichert. Paket-Keys gehören dem Paket: Wer einen davon in diesem Projekt ohne Owner-Rechte am Paket bearbeitet, erzeugt einen Vorschlag statt einer Änderung. Ein einzelner Key eines anderen Projekts ist kein Paket: Verknüpfen Sie ihn auf der Seite „Linked keys“.',
        },
        'sr-Latn-RS': {
          title: 'Pretplata na zajedničke pakete',
          body: 'Paket na koji je projekat pretplaćen dodaje svoje ključeve u datoteke ovog projekta, za jezike koje sam paket sadrži; svako dodavanje ili uklanjanje se odmah čuva. Ključevi paketa pripadaju paketu: izmena takvog ključa iz ovog projekta bez prava vlasnika (owner) na paketu pravi predlog umesto izmene. Pojedinačan ključ drugog projekta nije paket: povežite ga na stranici „Linked keys“.',
        },
      },
    },

    // ── Publish targets ─────────────────────────────────────────────────────────
    {
      selector: '[data-help="project-settings-targets"]',
      title: 'Review where the project publishes',
      body: 'The project’s target in the environment this Glossa serves — `dev`, `staging` or `prod`. Without it nothing can be published. A removed target stays in the list, marked “inactive”.',
      section: 'Publish targets',
      i18n: {
        'de-AT': {
          title: 'Prüfen, wohin das Projekt veröffentlicht',
          body: 'Das Ziel des Projekts in der Umgebung, die dieses Glossa bedient — `dev`, `staging` oder `prod`. Ohne Ziel kann nichts veröffentlicht werden. Ein entferntes Ziel bleibt in der Liste und ist als „inactive“ markiert.',
        },
        'sr-Latn-RS': {
          title: 'Pregled mesta na koje projekat objavljuje',
          body: 'Cilj projekta u okruženju koje ovaj Glossa opslužuje — `dev`, `staging` ili `prod`. Bez njega ništa se ne može objaviti. Uklonjeni cilj ostaje na listi, označen kao „inactive“.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-path"]',
      title: 'Check the exact URL',
      body: 'This is where a host application reads its bundles: the project’s folder `locales/<project>/` in this Glossa’s storage account, then one `{locale}.json` per locale. Without the file name it is the value of the application’s `VITE_I18N_BASE_URL`. Glossa derives it; it cannot be changed here.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die genaue URL prüfen',
          body: 'Hier liest eine Host-Anwendung ihre Bundles: der Projektordner `locales/<Projekt>/` im Storage-Account dieses Glossa, dann eine `{locale}.json` pro Sprache. Ohne Dateinamen ist das der Wert von `VITE_I18N_BASE_URL` der Anwendung. Glossa leitet sie ab; hier lässt sie sich nicht ändern.',
        },
        'sr-Latn-RS': {
          title: 'Provera tačnog URL-a',
          body: 'Odavde aplikacija domaćin čita svoje datoteke: folder projekta `locales/<projekat>/` u storage account-u ovog Glossa, a zatim po jedna `{locale}.json` za svaki jezik. Bez imena datoteke to je vrednost `VITE_I18N_BASE_URL` aplikacije. Glossa ga izvodi sama; ovde se ne može menjati.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-auto-publish"]',
      title: 'Publish automatically after changes',
      body: 'When on, Glossa publishes this target by itself once the content has changed and the number of seconds shown has passed without a further change, so a burst of saves becomes one release. Such releases show the trigger `auto` on the Releases screen. If one of the target’s gates refuses, nothing is published until the content passes it.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Nach Änderungen automatisch veröffentlichen',
          body: 'Ist der Schalter an, veröffentlicht Glossa dieses Ziel selbst, sobald sich der Inhalt geändert hat und die angezeigte Anzahl Sekunden ohne weitere Änderung vergangen ist — eine Reihe von Speichervorgängen wird so zu einem Release. Solche Releases tragen auf der Seite „Releases“ den Auslöser `auto`. Lehnt eine der Sperren des Ziels ab, wird nichts veröffentlicht, bis der Inhalt sie erfüllt.',
        },
        'sr-Latn-RS': {
          title: 'Automatsko objavljivanje posle izmena',
          body: 'Kada je uključeno, Glossa sama objavljuje ovaj cilj čim se sadržaj promeni i prođe prikazani broj sekundi bez nove izmene, pa niz čuvanja postaje jedno izdanje. Takva izdanja na ekranu „Releases“ imaju okidač `auto`. Ako neki od uslova cilja odbije objavljivanje, ništa se ne objavljuje dok sadržaj ne ispuni uslov.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-reviewed"]',
      title: 'Publish only reviewed translations',
      body: 'When on, a publish to this target is refused while any translation is still draft or machine, and the publish dialog shows how many values are in the way. Only an administrator can force a publish past this gate.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Nur geprüfte Übersetzungen veröffentlichen',
          body: 'Ist der Schalter an, wird ein Publish auf dieses Ziel abgelehnt, solange eine Übersetzung noch draft oder machine ist; der Publish-Dialog zeigt, wie viele Werte im Weg sind. Nur ein Administrator kann einen Publish an dieser Sperre vorbei erzwingen.',
        },
        'sr-Latn-RS': {
          title: 'Objavljivanje samo pregledanih prevoda',
          body: 'Kada je uključeno, objavljivanje na ovaj cilj se odbija dok je bilo koji prevod još u statusu draft ili machine, a dijalog za objavljivanje pokazuje koliko vrednosti smeta. Samo administrator može da nametne objavljivanje mimo ovog uslova.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-qa-clean"]',
      title: 'Publish only QA-clean translations',
      body: 'When on, a publish to this target is refused while any value has a QA error, such as placeholders that do not match the source. QA warnings do not block. This gate can only be switched on here, after the target exists.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Nur QA-fehlerfreie Übersetzungen veröffentlichen',
          body: 'Ist der Schalter an, wird ein Publish auf dieses Ziel abgelehnt, solange ein Wert einen QA-Fehler hat, etwa Platzhalter, die nicht zur Ausgangssprache passen. QA-Warnungen blockieren nicht. Diese Sperre lässt sich nur hier einschalten, nachdem das Ziel angelegt wurde.',
        },
        'sr-Latn-RS': {
          title: 'Objavljivanje samo prevoda bez QA grešaka',
          body: 'Kada je uključeno, objavljivanje na ovaj cilj se odbija dok bilo koja vrednost ima QA grešku, na primer placeholdere koji se ne poklapaju sa izvornim tekstom. QA upozorenja ne blokiraju. Ovaj uslov se može uključiti samo ovde, nakon što cilj postoji.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-remove"]',
      title: 'Remove a target',
      body: 'After you confirm, publishing to the target stops. Its releases and the files already in blob storage stay where they are, so an application reading them keeps working. A removed target is only switched off: it stays on the card as “inactive”, without this button. Only the project owner and administrators see it.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ein Ziel entfernen',
          body: 'Nach Ihrer Bestätigung wird auf dieses Ziel nicht mehr veröffentlicht. Seine Releases und die bereits im Blob-Storage liegenden Dateien bleiben, wo sie sind — eine Anwendung, die sie liest, funktioniert also weiter. Ein entferntes Ziel wird nur abgeschaltet: Es bleibt als „inactive“ auf der Karte, ohne diese Schaltfläche. Nur der Projekt-Owner und Administratoren sehen sie.',
        },
        'sr-Latn-RS': {
          title: 'Uklanjanje cilja',
          body: 'Nakon potvrde, objavljivanje na taj cilj prestaje. Njegova izdanja i datoteke koje su već u blob storage-u ostaju gde jesu, pa aplikacija koja ih čita nastavlja da radi. Uklonjen cilj je samo isključen: ostaje na kartici kao „inactive“, bez ovog dugmeta. Dugme vide samo vlasnik projekta (owner) i administratori.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-edit"]',
      title: 'Edit a target',
      body: 'Opens “Edit target” to switch a removed target back on. Removing only deactivates a target, so its environment stays taken until you reactivate it here instead of adding it again. Only the project owner and administrators see this button.',
      section: 'Publish targets',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ein Ziel bearbeiten',
          body: 'Öffnet „Edit target“, um ein entferntes Ziel wieder einzuschalten. Entfernen deaktiviert ein Ziel nur, seine Umgebung bleibt also belegt, bis Sie es hier wieder aktivieren, statt es neu anzulegen. Nur der Projekt-Owner und Administratoren sehen diese Schaltfläche.',
        },
        'sr-Latn-RS': {
          title: 'Izmena cilja',
          body: 'Otvara „Edit target“ da biste ponovo uključili uklonjen cilj. Uklanjanje cilj samo deaktivira, pa njegovo okruženje ostaje zauzeto dok ga ovde ponovo ne aktivirate umesto da ga dodajete iznova. Ovo dugme vide samo vlasnik projekta (owner) i administratori.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-target-add"]',
      title: 'Add a publish target',
      body: 'Opens the “Add publish target” dialog for the project’s target in this environment. Only the project owner and administrators see this button.',
      section: 'Publish targets',
      i18n: {
        'de-AT': {
          title: 'Ein Veröffentlichungsziel hinzufügen',
          body: 'Öffnet den Dialog „Add publish target“ für das Ziel des Projekts in dieser Umgebung. Nur der Projekt-Owner und Administratoren sehen diese Schaltfläche.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje cilja objavljivanja',
          body: 'Otvara dijalog „Add publish target“ za cilj projekta u ovom okruženju. Ovo dugme vide samo vlasnik projekta (owner) i administratori.',
        },
      },
    },

    // ── New target dialog ───────────────────────────────────────────────────────
    {
      selector: '[data-help="target-new-path"]',
      title: 'Check where the files go',
      body: 'The environment this Glossa serves and the URL the files will have: `locales/<project>/` in this Glossa’s storage account. Nothing needs to be typed. Only production counts as `prod`: owners may publish it, translators only when “Translators may publish the prod target” is on, developers never.',
      section: 'New target dialog',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Prüfen, wohin die Dateien gehen',
          body: 'Die Umgebung, die dieses Glossa bedient, und die URL der Dateien: `locales/<Projekt>/` im Storage-Account dieses Glossa. Eingetippt werden muss nichts. Nur production gilt als `prod`: Owner dürfen dorthin veröffentlichen, Translator nur, wenn „Translators may publish the prod target“ eingeschaltet ist, Developer nie.',
        },
        'sr-Latn-RS': {
          title: 'Provera odredišta datoteka',
          body: 'Okruženje koje ovaj Glossa opslužuje i URL datoteka: `locales/<projekat>/` u storage account-u ovog Glossa. Ništa ne mora da se kuca. Samo production se smatra `prod`: vlasnici (owner) smeju da ga objave, prevodioci (translator) samo ako je uključeno „Translators may publish the prod target“, a developeri nikada.',
        },
      },
    },
    {
      selector: '[data-help="target-new-reviewed"]',
      title: 'Require reviewed translations',
      body: 'The same gate as the “Only reviewed translations” switch on the target row: a publish is refused while anything is still draft or machine. You can change it later on the card.',
      section: 'New target dialog',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Geprüfte Übersetzungen verlangen',
          body: 'Dieselbe Sperre wie der Schalter „Only reviewed translations“ in der Zeile des Ziels: Ein Publish wird abgelehnt, solange noch etwas draft oder machine ist. Sie können das später auf der Karte ändern.',
        },
        'sr-Latn-RS': {
          title: 'Zahtevanje pregledanih prevoda',
          body: 'Isti uslov kao prekidač „Only reviewed translations“ u redu cilja: objavljivanje se odbija dok je bilo šta još u statusu draft ili machine. Kasnije to možete promeniti na kartici.',
        },
      },
    },
    {
      selector: '[data-help="target-new-auto-publish"]',
      title: 'Publish automatically',
      body: 'The same as the “Auto-publish” switch on the target row; a target created here waits 30 seconds after the last change before it publishes itself. You can change it later on the card.',
      section: 'New target dialog',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Automatisch veröffentlichen',
          body: 'Entspricht dem Schalter „Auto-publish“ in der Zeile des Ziels; ein hier angelegtes Ziel wartet nach der letzten Änderung 30 Sekunden, bevor es sich selbst veröffentlicht. Sie können das später auf der Karte ändern.',
        },
        'sr-Latn-RS': {
          title: 'Automatsko objavljivanje',
          body: 'Isto kao prekidač „Auto-publish“ u redu cilja; cilj kreiran ovde čeka 30 sekundi posle poslednje izmene pre nego što se sam objavi. Kasnije to možete promeniti na kartici.',
        },
      },
    },
    {
      selector: '[data-help="target-new-create"]',
      title: 'Create the target',
      body: '“Create” saves the target and adds it to the card. It stays disabled while the project already has its target in this environment, a removed one included; any other refusal from the server appears at the top of the dialog.',
      section: 'New target dialog',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Das Ziel anlegen',
          body: '„Create“ speichert das Ziel und fügt es der Karte hinzu. Die Schaltfläche bleibt gesperrt, solange das Projekt sein Ziel in dieser Umgebung schon hat, auch ein entferntes; jede andere Ablehnung durch den Server steht oben im Dialog.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje cilja',
          body: '„Create“ čuva cilj i dodaje ga na karticu. Dugme je zaključano dok projekat već ima svoj cilj u ovom okruženju, uključujući i uklonjen; svako drugo odbijanje servera prikazuje se na vrhu dijaloga.',
        },
      },
    },

    // ── Edit target dialog ──────────────────────────────────────────────────────
    {
      selector: '[data-help="target-edit-path"]',
      title: 'See where it publishes',
      body: 'The exact URL the target writes to. It is derived from this Glossa and the project, so it is shown, not edited.',
      section: 'Edit target dialog',
      context: 'target-edit-dialog',
      i18n: {
        'de-AT': {
          title: 'Sehen, wohin es veröffentlicht',
          body: 'Die genaue URL, in die das Ziel schreibt. Sie ergibt sich aus diesem Glossa und dem Projekt, wird also angezeigt, nicht bearbeitet.',
        },
        'sr-Latn-RS': {
          title: 'Gde cilj objavljuje',
          body: 'Tačan URL u koji cilj upisuje. Izvodi se iz ovog Glossa i projekta, pa se prikazuje, a ne menja.',
        },
      },
    },
    {
      selector: '[data-help="target-edit-active"]',
      title: 'Switch a target on or off',
      body: 'On, the target can be published to again; off is the same as removing it. A removed target comes back with its code, settings and releases.',
      section: 'Edit target dialog',
      context: 'target-edit-dialog',
      i18n: {
        'de-AT': {
          title: 'Ein Ziel ein- oder ausschalten',
          body: 'Eingeschaltet kann wieder auf das Ziel veröffentlicht werden; ausgeschaltet entspricht dem Entfernen. Ein entferntes Ziel kommt mit Code, Einstellungen und Releases zurück.',
        },
        'sr-Latn-RS': {
          title: 'Uključivanje i isključivanje cilja',
          body: 'Uključeno, na cilj se ponovo može objavljivati; isključeno je isto što i uklanjanje. Uklonjen cilj se vraća sa svojim kodom, podešavanjima i izdanjima.',
        },
      },
    },
    {
      selector: '[data-help="target-edit-save"]',
      title: 'Save the change',
      body: '“Save” stays locked until you switch the target on or off; a refusal from the server shows at the top of the dialog.',
      section: 'Edit target dialog',
      context: 'target-edit-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Änderung speichern',
          body: '„Save“ bleibt gesperrt, bis Sie das Ziel ein- oder ausschalten; eine Ablehnung durch den Server steht oben im Dialog.',
        },
        'sr-Latn-RS': {
          title: 'Čuvanje izmene',
          body: '„Save“ je zaključano dok ne uključite ili isključite cilj; odbijanje servera prikazuje se na vrhu dijaloga.',
        },
      },
    },

    // ── Export options ──────────────────────────────────────────────────────────
    {
      selector: '[data-help="project-settings-export"]',
      title: 'Change what goes into a bundle',
      body: 'These options apply to every target of the project and show in the files from the next publish on. Each switch is saved as soon as you flip it; only the owner and administrators can change them.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Festlegen, was in ein Bundle kommt',
          body: 'Diese Optionen gelten für alle Ziele des Projekts und wirken sich ab dem nächsten Publish auf die Dateien aus. Jeder Schalter wird sofort gespeichert; ändern können sie nur der Owner und Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Određivanje sadržaja paketa datoteka',
          body: 'Ove opcije važe za sve ciljeve projekta i vide se u datotekama od sledećeg objavljivanja. Svaki prekidač se čuva čim ga promenite; menjati ih mogu samo vlasnik (owner) i administratori.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-export-sort"]',
      title: 'Choose the key order',
      body: '`key` sorts the file by key name, which keeps it stable and gives git-friendly diffs. `position` reproduces the order in which the keys were created.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Die Reihenfolge der Keys wählen',
          body: '`key` sortiert die Datei nach Key-Namen, das hält sie stabil und ergibt git-freundliche Diffs. `position` gibt die Reihenfolge wieder, in der die Keys angelegt wurden.',
        },
        'sr-Latn-RS': {
          title: 'Izbor redosleda ključeva',
          body: '`key` sortira datoteku po nazivu ključa, što je čini stabilnom i daje pregledne razlike u git-u. `position` zadržava redosled kojim su ključevi kreirani.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-export-machine"]',
      title: 'Export machine translations',
      body: 'On by default: an AI translation beats showing English to a Croatian user. Switched off, values with the status machine are left out of the files, so the application falls back to another language for those keys.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Maschinenübersetzungen exportieren',
          body: 'Standardmäßig an: Eine KI-Übersetzung ist besser, als einem kroatischen Benutzer Englisch zu zeigen. Ausgeschaltet fehlen Werte mit dem Status machine in den Dateien, und die Anwendung greift für diese Keys auf eine andere Sprache zurück.',
        },
        'sr-Latn-RS': {
          title: 'Izvoz mašinskih prevoda',
          body: 'Podrazumevano uključeno: AI prevod je bolji nego da se hrvatskom korisniku prikaže engleski. Kada je isključeno, vrednosti sa statusom machine se izostavljaju iz datoteka, pa aplikacija za te ključeve prelazi na drugi jezik.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-export-empty"]',
      title: 'Include untranslated keys',
      body: 'Off by default, and normally left off. i18next only falls back to another language when a key is absent from the file; with this on, untranslated keys are written as empty strings and no longer fall back.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Unübersetzte Keys aufnehmen',
          body: 'Standardmäßig aus und normalerweise auch so belassen. i18next greift nur dann auf eine andere Sprache zurück, wenn ein Key in der Datei fehlt; ist der Schalter an, werden unübersetzte Keys als leere Zeichenketten geschrieben und fallen nicht mehr zurück.',
        },
        'sr-Latn-RS': {
          title: 'Uključivanje neprevedenih ključeva',
          body: 'Podrazumevano isključeno i obično tako i ostaje. i18next prelazi na drugi jezik samo kada ključ ne postoji u datoteci; kada je ovo uključeno, neprevedeni ključevi se upisuju kao prazni stringovi i više ne prelaze na drugi jezik.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-ai-auto-translate"]',
      title: 'Pre-translate new keys with AI',
      body: 'Switched off for now: the switch stays greyed out until AI translation is available. When on, creating a key that has a source value queues an AI job that fills the key’s empty cells in the project’s other locales, with the status machine. Nothing happens when no AI provider is configured.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Neue Keys mit KI vorübersetzen',
          body: 'Derzeit abgeschaltet: Der Schalter bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Ist der Schalter an, startet das Anlegen eines Keys mit Ausgangswert einen KI-Auftrag, der die leeren Zellen dieses Keys in den anderen Projektsprachen mit dem Status machine füllt. Ist kein KI-Anbieter konfiguriert, passiert nichts.',
        },
        'sr-Latn-RS': {
          title: 'AI predprevod novih ključeva',
          body: 'Trenutno isključeno: prekidač ostaje siv dok AI prevod ne postane dostupan. Kada je uključeno, kreiranje ključa sa izvornom vrednošću pokreće AI posao koji popunjava prazna polja tog ključa na ostalim jezicima projekta, sa statusom machine. Ako AI provajder nije konfigurisan, ništa se ne dešava.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-prod-translator"]',
      title: 'Let translators publish prod',
      body: 'Off by default: only the owner and administrators may publish the target with the code `prod`. Switch it on to let translators publish it too; the Publish button on the Releases screen follows it. Developers can publish every other target, but never `prod`.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Translator dürfen prod veröffentlichen',
          body: 'Standardmäßig aus: Nur der Owner und Administratoren dürfen das Ziel mit dem Code `prod` veröffentlichen. Schalten Sie es ein, damit auch Translator es dürfen; die Schaltfläche „Publish“ auf der Seite „Releases“ richtet sich danach. Developer dürfen alle anderen Ziele veröffentlichen, aber nie `prod`.',
        },
        'sr-Latn-RS': {
          title: 'Dozvola prevodiocima da objave prod',
          body: 'Podrazumevano isključeno: samo vlasnik (owner) i administratori smeju da objave cilj sa kodom `prod`. Uključite ovo da bi i prevodioci (translator) mogli da ga objave; dugme „Publish“ na ekranu „Releases“ prati ovo podešavanje. Developeri mogu da objave svaki drugi cilj, ali nikada `prod`.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-description"]',
      title: 'Describe the application',
      body: 'One sentence about what this application is. It is sent to the AI as context when it translates for this project, and it is saved when you leave the field.',
      section: 'Export options',
      i18n: {
        'de-AT': {
          title: 'Die Anwendung beschreiben',
          body: 'Ein Satz darüber, was diese Anwendung ist. Er wird der KI als Kontext mitgegeben, wenn sie für dieses Projekt übersetzt, und beim Verlassen des Feldes gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Opis aplikacije',
          body: 'Jedna rečenica o tome šta je ova aplikacija. Šalje se AI-ju kao kontekst kada prevodi za ovaj projekat, a čuva se kada napustite polje.',
        },
      },
    },

    // ── Coverage and prefixes ───────────────────────────────────────────────────
    {
      selector: '[data-help="project-settings-coverage"]',
      title: 'Read the coverage per locale',
      body: 'Counted in translation units, so a plural key counts once per plural form of the locale. The percentage includes reviewed, draft and machine values, and the reviewed share is drawn over the bar in its own colour. Hover a bar for the exact split, empty cells included.',
      section: 'Coverage and prefixes',
      i18n: {
        'de-AT': {
          title: 'Die Abdeckung pro Sprache lesen',
          body: 'Gezählt wird in Übersetzungseinheiten, ein Plural-Key zählt also einmal pro Pluralform der Sprache. Der Prozentwert enthält reviewed-, draft- und machine-Werte; der geprüfte Anteil ist in einer eigenen Farbe über den Balken gelegt. Fahren Sie mit der Maus über einen Balken, um die genaue Aufteilung samt leerer Zellen zu sehen.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje pokrivenosti po jeziku',
          body: 'Broji se u jedinicama prevoda, pa se ključ sa množinom broji jednom za svaki oblik množine jezika. Procenat obuhvata vrednosti reviewed, draft i machine, a udeo pregledanih je iscrtan preko trake sopstvenom bojom. Zadržite pokazivač iznad trake da vidite tačnu podelu, uključujući prazna polja.',
        },
      },
    },
    {
      selector: '[data-help="project-settings-prefixes"]',
      title: 'See which key prefixes the project uses',
      body: 'Every key prefix that resolves to this project or to a package it subscribes to, with its number of keys; package prefixes are coloured differently. The first segment of a key name decides who owns the key, and a prefix nobody has registered is refused when a key is created.',
      section: 'Coverage and prefixes',
      i18n: {
        'de-AT': {
          title: 'Sehen, welche Key-Präfixe das Projekt nutzt',
          body: 'Jedes Key-Präfix, das zu diesem Projekt oder zu einem abonnierten Paket gehört, mit der Anzahl seiner Keys; Paket-Präfixe sind andersfarbig. Das erste Segment eines Key-Namens bestimmt, wem der Key gehört, und ein Präfix, das niemand registriert hat, wird beim Anlegen eines Keys abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Pregled prefiksa ključeva koje projekat koristi',
          body: 'Svaki prefiks ključeva koji pripada ovom projektu ili paketu na koji je pretplaćen, sa brojem ključeva; prefiksi paketa su drugačije obojeni. Prvi segment naziva ključa određuje kome ključ pripada, a prefiks koji niko nije registrovao odbija se pri kreiranju ključa.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Projekteinstellungen',
      intro:
        'Sprachen, Paket-Abonnements, Veröffentlichungsziele und Exportoptionen eines Projekts. Jede davon verändert, was in den veröffentlichten Dateien landet; ändern können sie deshalb nur der Projekt-Owner und Administratoren, alle anderen sehen sie schreibgeschützt.',
      sections: {
        'Reading the page': 'Die Seite lesen',
        'Locales and packages': 'Sprachen und Pakete',
        'Publish targets': 'Veröffentlichungsziele',
        'New target dialog': 'Dialog „Neues Ziel“',
        'Edit target dialog': 'Dialog „Ziel bearbeiten“',
        'Export options': 'Exportoptionen',
        'Coverage and prefixes': 'Abdeckung und Präfixe',
      },
      contexts: {
        'target-dialog':
          'Klicken Sie auf der Karte „Publish targets“ auf „Add target“ (nur Projekt-Owner und Administratoren).',
        'target-edit-dialog':
          'Klicken Sie auf der Karte „Publish targets“ auf den Stift neben einem Ziel (nur Projekt-Owner und Administratoren).',
      },
      tips: [
        'Jede Änderung auf dieser Seite wird sofort gespeichert — es gibt keine Schaltfläche zum Speichern. Einzige Ausnahme ist die Beschreibung: Sie wird beim Verlassen des Feldes gespeichert.',
        'Sprachen, Paket-Abonnements und Exportoptionen ändern die Dateien erst beim nächsten Publish. Jede dieser Änderungen erhöht die Inhaltsversion, ein Ziel mit eingeschaltetem Auto-Publish veröffentlicht sie also von selbst.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Podešavanja projekta',
      intro:
        'Jezici, pretplate na pakete, ciljevi objavljivanja i opcije izvoza jednog projekta. Svako od njih menja ono što završava u objavljenim datotekama, pa ih mogu menjati samo vlasnik projekta (owner) i administratori; svi ostali ih vide samo za čitanje.',
      sections: {
        'Reading the page': 'Čitanje stranice',
        'Locales and packages': 'Jezici i paketi',
        'Publish targets': 'Ciljevi objavljivanja',
        'New target dialog': 'Dijalog za novi cilj',
        'Edit target dialog': 'Dijalog za izmenu cilja',
        'Export options': 'Opcije izvoza',
        'Coverage and prefixes': 'Pokrivenost i prefiksi',
      },
      contexts: {
        'target-dialog':
          'Kliknite na „Add target“ na kartici „Publish targets“ (samo vlasnici projekta i administratori).',
        'target-edit-dialog':
          'Kliknite na olovku pored cilja na kartici „Publish targets“ (samo vlasnici projekta i administratori).',
      },
      tips: [
        'Svaka promena na ovoj stranici se čuva odmah — nema dugmeta za čuvanje. Jedini izuzetak je opis: on se čuva kada napustite polje.',
        'Jezici, pretplate na pakete i opcije izvoza menjaju datoteke tek pri sledećem objavljivanju. Svaka od tih promena povećava verziju sadržaja, pa je cilj sa uključenim automatskim objavljivanjem sam objavljuje.',
      ],
    },
  },
};
